import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiMiddleware from "../../../api/apiMiddleware";
import { listRentalsAdmin } from "./rentalService";
import useTableSearch from "../../../hooks/useTableSearch";
import { Link } from "react-router-dom";
import Pagination from "../../common/Pagination";
import Toster from "../../common/Toster";
import RentalApprovalSettings from "./RentalApprovalSettings";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  TableActionButton,
  TableActionsCell,
} from "../../common/adminTable";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";

const Rentals = () => {
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, activeSearch } = useTableSearch();
  // Default: show submitted items only (drafts stay hidden until supplier finishes setup).
  const [statusFilter, setStatusFilter] = useState("1"); // submitted/active
  // Default: show all active approvals (Pending + Approved), hide Rejected.
  const [approvalFilter, setApprovalFilter] = useState("all_active");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");
  const [showToaster, setShowToaster] = useState(false);
  const [approvalSettingsOpen, setApprovalSettingsOpen] = useState(false);
  const [selectedRentalForApproval, setSelectedRentalForApproval] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  const filters = {
    search: activeSearch || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    is_approved: approvalFilter || undefined,
    supplier_id: supplierFilter !== "all" ? supplierFilter : undefined,
  };

  const { data: response, isLoading, error } = useQuery({
    queryKey: ["rentals-admin", currentPage, filters],
    queryFn: () => listRentalsAdmin(currentPage, filters),
    keepPreviousData: true,
  });

  const rentals = response?.data?.data || [];
  const current_page = response?.data?.current_page || 1;
  const last_page = response?.data?.last_page || 1;
  const total = response?.data?.total || 0;
  const per_page = response?.data?.per_page || 10;
  const from = total === 0 ? 0 : (current_page - 1) * per_page + 1;
  const to = Math.min(current_page * per_page, total);

  const { data: suppliersData } = useQuery({
    queryKey: ["allSuppliersForRentals"],
    queryFn: async () => {
      const r = await apiMiddleware.get(`admin-suppliers?per_page=200`);
      return r.data?.data?.data || [];
    },
  });

  const suppliers = suppliersData || [];

  const openApprovalSettings = (r) => {
    setSelectedRentalForApproval(r);
    setApprovalSettingsOpen(true);
  };

  const badge = (isApproved) => {
    if (Number(isApproved) === 1)
      return <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-semibold">Approved</span>;
    if (Number(isApproved) === 2)
      return <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 font-semibold">Rejected</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800 font-semibold">Pending</span>;
  };

  const pageMeta = ADMIN_PAGE_META.rentals;

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="text-primary font-medium">Loading rentals...</span>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }
  if (error) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="fi fi-rr-exclamation text-red-500 text-4xl mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading rentals
              </h3>
              <p className="text-gray-600">{error.message}</p>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout>
      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex-1 flex gap-3 flex-col sm:flex-row">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search rentals..."
                className="flex-1"
              />

              <select
                value={approvalFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setApprovalFilter(e.target.value);
                }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="all_active">All</option>
                <option value="0">Pending</option>
                <option value="1">Approved</option>
                <option value="2">Rejected</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setStatusFilter(e.target.value);
                }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="all">All status</option>
                <option value="0">Draft</option>
                <option value="1">Submitted/Active</option>
              </select>

              <select
                value={supplierFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSupplierFilter(e.target.value);
                }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="all">All suppliers</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.email || `Supplier #${s.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm text-gray-500">
              {total} item{total === 1 ? "" : "s"}
            </div>
          </div>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={{
                  currentPage: current_page,
                  lastPage: last_page,
                  perPage: per_page,
                  total,
                  from,
                  to,
                }}
                onPageChange={setCurrentPage}
              />
            </AdminTableFooter>
          }
        >
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Rental</AdminTableTh>
            <AdminTableTh>Supplier</AdminTableTh>
            <AdminTableTh>Qty</AdminTableTh>
            <AdminTableTh>Stage</AdminTableTh>
            <AdminTableTh>Approval</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {rentals.length === 0 ? (
              <TableEmptyRow
                colSpan={7}
                emptyMessage="No rentals found"
                searchQuery={activeSearch}
              />
            ) : (
              rentals.map((r, idx) => (
                <AdminTableRow key={r.id}>
                  <AdminTableCell>
                    {(current_page - 1) * per_page + idx + 1}
                  </AdminTableCell>
                  <AdminTableCell className="font-medium">
                    <div className="min-w-0">
                      <div className="truncate">{r.title || "Untitled"}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {r.brand || "—"} {r.subtitle ? `• ${r.subtitle}` : ""}
                      </div>
                    </div>
                  </AdminTableCell>
                  <AdminTableCell>
                    {r.supplier?.name || r.supplier?.email || "—"}
                  </AdminTableCell>
                  <AdminTableCell>{r.quantity}</AdminTableCell>
                  <AdminTableCell>{r.current_stage}</AdminTableCell>
                  <AdminTableCell>{badge(r.is_approved)}</AdminTableCell>
                  <TableActionsCell>
                    <TableActionButton
                      as={Link}
                      to={`/rentals/${r.id}`}
                      variant="edit"
                      showLabel={false}
                      ariaLabel="Update details"
                    />
                    <TableActionButton
                      variant="settings"
                      showLabel={false}
                      ariaLabel="Approval & status"
                      onClick={() => openApprovalSettings(r)}
                    />
                  </TableActionsCell>
                </AdminTableRow>
              ))
            )}
          </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      {showToaster && (
        <Toster
          message={toasterMessage}
          type={toasterType}
          duration={4000}
          onClose={() => setShowToaster(false)}
        />
      )}



      {approvalSettingsOpen && selectedRentalForApproval && (
        <RentalApprovalSettings
          open={approvalSettingsOpen}
          onClose={() => {
            setApprovalSettingsOpen(false);
            setSelectedRentalForApproval(null);
          }}
          rentalData={selectedRentalForApproval}
          onNotify={({ type, message }) => {
            setToasterMessage(message);
            setToasterType(type);
            setShowToaster(true);
          }}
        />
      )}
    </AdminPageLayout>
  );
};

export default Rentals;

